import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { formatOtherContacts, type TextInbox, type TextInboxItem } from '../data';

const MAX_INDIVIDUAL_TOASTS = 3;
const TOAST_DURATION_MS = 10_000;

/**
 * Toasts each text that arrives after the view loads. Texts already unread on
 * load only get the badge, and the thread the rep has open is skipped.
 */
export function useNewTextToasts(
	inbox: TextInbox,
	{ onOpen, openThreadE164 }: { onOpen: (item: TextInboxItem) => void; openThreadE164: string | undefined }
): void {
	const seenRef = useRef<Set<string> | null>(null);
	const onOpenRef = useRef(onOpen);
	onOpenRef.current = onOpen;

	useEffect(() => {
		if (!inbox.settled) return;
		if (!seenRef.current) {
			seenRef.current = new Set(inbox.items.map(item => item.latestMessageKey));
			return;
		}
		const seen = seenRef.current;
		const fresh = inbox.items.filter(item => !seen.has(item.latestMessageKey));
		for (const item of fresh) seen.add(item.latestMessageKey);

		const toAnnounce = fresh.filter(item => !item.e164 || item.e164 !== openThreadE164);
		if (toAnnounce.length > MAX_INDIVIDUAL_TOASTS) {
			toast(`${toAnnounce.length} new texts`, {
				description: 'Open the texts icon at the top to see them.',
				duration: TOAST_DURATION_MS,
			});
			return;
		}
		for (const item of toAnnounce) {
			toast(`New text from ${item.contactName}`, {
				id: `focused-view-text-${item.conversationId}`,
				description:
					[item.companyName, formatOtherContacts(item.otherContactCount), item.snippet].filter(Boolean).join(' · ') ||
					undefined,
				duration: TOAST_DURATION_MS,
				action: item.e164 || item.companyId ? { label: 'Open', onClick: () => onOpenRef.current(item) } : undefined,
			});
		}
	}, [inbox, openThreadE164]);
}
